<script setup lang="ts">
import { computed, ref } from 'vue';
import type { StudyModel } from '@/models/study';
import StudySelectorContent from './StudySelectorContent.vue';
import { Popup } from '@/components';
import { useRouter } from 'vue-router';
import { LocalStorageUtils, DATA_VIEW_FILTER, routeConstants } from '@/utils';
import { useStudyStore } from '@/stores/study';
import { usePipelineStore } from '@/stores/pipeline';

const studyStore = useStudyStore();
const pipelineStore = usePipelineStore();

const selectedStudy = computed(() => studyStore.studies?.find(study => study.identifier === studyStore.selectedStudyId));
const visible = ref(false);
const selectorRef = ref<HTMLElement | null>(null);

const xValue = ref('0px');
const yValue = ref('0px');

const toggle = () => {
  visible.value = !visible.value;
  const bounds = selectorRef.value?.getBoundingClientRect();
  if (bounds) {
    xValue.value = `${bounds.x}px`;
    yValue.value = `${bounds.y + bounds.height}px`;
  }
};

const router = useRouter();
const setSelected = (item: StudyModel) => {
  if (item.longitudinal === true) {
    if (item?.flags?.preferences?.dataViewFilter) {
      LocalStorageUtils.setDataViewFilter(item.identifier, item?.flags?.preferences?.dataViewFilter);
    } else {
      LocalStorageUtils.setDataViewFilter(item.identifier, DATA_VIEW_FILTER.CASES);
    }
  } else {
    LocalStorageUtils.setDataViewFilter(item.identifier, DATA_VIEW_FILTER.ENTRIES);
  }
  studyStore.setStudy(item);
  pipelineStore.resetStore();
  
  visible.value = !visible.value;
  router.replace({
    name: routeConstants.DATA.name,
    params: { studyIdentifier: item.identifier }
  });
};

</script>

<template>
  <div class="study-selector" @click="toggle()">
    <div class="study-selector-header" ref="selectorRef">
      <div class="study-label">
        <div class="study-selector-label">{{ selectedStudy?.title }}</div>
        <div class="part11badge" v-if="selectedStudy?.flags?.cfrPart11">21 CFR Part 11 Active</div>
      </div>
      <FontAwesomeIcon :icon="['fas', 'caret-down']" class="caret-down" />
    </div>

    <Popup :visible="visible"
      :x="xValue"
      :y="yValue"
      @outsidePopupClick="toggle">
      <template #popupContent>
        <StudySelectorContent :studies="studyStore.studies" @studyClick="setSelected"/>
      </template>
    </Popup>
  </div>
</template>

<style scoped lang="scss">
.study-selector {
  padding: 8px 10%;
  &:hover {
    cursor: pointer;
  }
}
.caret-down {
  font-size: 14px;
  color: hsla(0, 0%, 0%, 0.54);
  padding-right: 8px;
}
.study-selector-header {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
}
.study-selector-label {
  background-color: transparent;
  border: none;
  padding-top: 4px;
  padding-bottom: 4px;
  padding-right: 8px;
  font-size: 16px;
  font-weight: 400;
  max-width: 205px;

  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}
.part11badge {
  display: inline-block;
  padding: 0.3em 0.7em;
  border-radius: 0.25em;
  font-family: 'Segoe UI', Arial, sans-serif;
  font-size: 0.85em;
  font-weight: bold;
  text-transform: uppercase;
  color: #ffffff;
  background-color: #28a745;
  text-align: center;
  line-height: 1;
  white-space: nowrap;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
</style>
